/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import { useEffect, useState } from 'react';

/* Import configuration */
import {
    DEFAULT_PLOTLY_GROUP,
    DEFAULT_PLOTLY_METER,
    DEFAULT_PLOTLY_RANGE,
    DEFAULT_PLOTLY_REFERENCE,
    createStoredPlotlyRequest,
    getPlotlyGroupConfig,
    getPlotlyRequestMeter,
    getPlotlySensorDescription,
    getPreferredPlotlyReference,
    type PlotlyGroupKey,
} from '@/config/eeCheckConfig';

/* Import types */
import type { PlotlySubsensor } from '@/types/ui';

export function usePlotlyDemo(sensorCatalogVersion: number) {
    const [selectedGroup, setSelectedGroup] = useState<PlotlyGroupKey>(DEFAULT_PLOTLY_GROUP);
    const [selectedMeter, setSelectedMeter] = useState<string>(DEFAULT_PLOTLY_METER);
    const [selectedReference, setSelectedReference] = useState<string>(DEFAULT_PLOTLY_REFERENCE);
    const [selectedSubsensor, setSelectedSubsensor] = useState<PlotlySubsensor>('');
    const [selectedRange, setSelectedRange] = useState<string>(DEFAULT_PLOTLY_RANGE);
    const activePlotlyGroup = getPlotlyGroupConfig(selectedGroup);
    const activePlotlyMeterConfig = activePlotlyGroup.meters[selectedMeter] ?? activePlotlyGroup.meters[Object.keys(activePlotlyGroup.meters)[0]];
    const activePlotlyReferences = Object.keys(activePlotlyMeterConfig.reference);
    const activePlotlyRangeOptions = activePlotlyMeterConfig.reference[selectedReference]?.ranges ?? activePlotlyMeterConfig.reference[activePlotlyReferences[0]]?.ranges ?? [];
    const activePlotlySubsensorOptions = activePlotlyMeterConfig.reference[selectedReference]?.subsensors ?? [];
    const plotlyRequestMeter = getPlotlyRequestMeter(
        selectedGroup,
        selectedMeter,
        selectedReference,
        selectedSubsensor,
    );
    const plotlyRequest = createStoredPlotlyRequest(
        plotlyRequestMeter,
        selectedReference,
        selectedRange,
    );
    const selectedSensorDescription = getPlotlySensorDescription(selectedGroup, selectedMeter, selectedSubsensor || undefined);

    useEffect(() => {
        setSelectedGroup(DEFAULT_PLOTLY_GROUP);
    }, [sensorCatalogVersion]);

    useEffect(() => {
        const nextMeter = Object.keys(activePlotlyGroup.meters).find((meter) =>
            Object.keys(activePlotlyGroup.meters[meter].reference).some(
                (reference) => reference !== 'pv_plots',
            ),
        ) ?? Object.keys(activePlotlyGroup.meters)[0] ?? '';
        const nextReference = nextMeter
            ? getPreferredPlotlyReference(activePlotlyGroup.meters[nextMeter].reference)
            : '';
        const nextRange = nextMeter && nextReference
            ? activePlotlyGroup.meters[nextMeter].reference[nextReference].ranges[0] ?? ''
            : '';

        setSelectedMeter(nextMeter);
        setSelectedReference(nextReference);
        setSelectedSubsensor('');
        setSelectedRange(nextRange);
    }, [activePlotlyGroup.meters, selectedGroup, sensorCatalogVersion]);

    useEffect(() => {
        const nextReference = getPreferredPlotlyReference(activePlotlyMeterConfig.reference);
        const nextRange = nextReference
            ? activePlotlyMeterConfig.reference[nextReference].ranges[0] ?? ''
            : '';

        setSelectedReference(nextReference);
        setSelectedSubsensor('');
        setSelectedRange(nextRange);
    }, [activePlotlyMeterConfig.reference, selectedMeter]);

    useEffect(() => {
        const nextRange = activePlotlyMeterConfig.reference[selectedReference]?.ranges[0] ?? '';

        setSelectedSubsensor(activePlotlySubsensorOptions[0] ?? '');
        setSelectedRange(nextRange);
    }, [activePlotlyMeterConfig.reference, activePlotlySubsensorOptions, selectedReference]);

    return {
        activePlotlyGroup,
        activePlotlyRangeOptions,
        activePlotlyReferences,
        activePlotlySubsensorOptions,
        plotlyRequest,
        plotlyRequestMeter,
        selectedGroup,
        selectedMeter,
        selectedRange,
        selectedReference,
        selectedSensorDescription,
        selectedSubsensor,
        setSelectedGroup,
        setSelectedMeter,
        setSelectedRange,
        setSelectedReference,
        setSelectedSubsensor,
    };
}
